/**
 * @file AssetGridView.js
 * @description Grid view with asset cards for the Asset Library.
 */

import styles from '../AssetLibrary.module.scss';
import classNames from 'classnames';
import { getFileCategory } from '../../helpers/FileUtils';
import { useState, useEffect, useRef } from 'react';
import { Avatar } from '@mui/material';
import { getIndividualRecord } from '../../../services/individualService';
import { ResourceProgressStatusEnum } from '../../../utils/data/enums';
import { getText } from '../../../i18n';

function isAssetPrivate(asset) {
    return asset.assetTypeId === 3;
}

const RELEASE_VIDEO_THUMBNAILS_EVENT = 'asset-library:release-video-thumbnails';
const VIDEO_THUMBNAIL_PREVIEW_SECOND = 3;

const VideoThumbnail = ({ videoUrl, width = 186, height = 168 }) => {
    const [thumbnail, setThumbnail] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const video = document.createElement('video');
        const abortController = new AbortController();
        const { signal } = abortController;

        const releaseVideo = () => {
            video.pause();
            video.removeAttribute('src');
            video.load();
        };

        const abortGeneration = () => {
            if (!signal.aborted) {
                abortController.abort();
            }
            releaseVideo();
        };

        const waitForEvent = eventName => {
            return new Promise((resolve, reject) => {
                if (signal.aborted) {
                    reject(new Error('Thumbnail generation aborted'));
                    return;
                }

                const timeoutId = window.setTimeout(() => {
                    cleanupListeners();
                    reject(new Error(`Timed out waiting for ${eventName}`));
                }, 5000);

                const handleSuccess = () => {
                    cleanupListeners();
                    resolve();
                };

                const handleError = () => {
                    cleanupListeners();
                    reject(new Error(`Failed while waiting for ${eventName}`));
                };

                const handleAbort = () => {
                    cleanupListeners();
                    reject(new Error('Thumbnail generation aborted'));
                };

                const cleanupListeners = () => {
                    window.clearTimeout(timeoutId);
                    video.removeEventListener(eventName, handleSuccess);
                    video.removeEventListener('error', handleError);
                    signal.removeEventListener('abort', handleAbort);
                };

                video.addEventListener(eventName, handleSuccess, {
                    once: true,
                });
                video.addEventListener('error', handleError, { once: true });
                signal.addEventListener('abort', handleAbort, { once: true });
            });
        };

        const generateThumbnail = async () => {
            try {
                setThumbnail(null);
                video.crossOrigin = 'anonymous';
                video.muted = true;
                video.playsInline = true;
                video.preload = 'metadata';
                video.src = videoUrl;
                video.load();

                await waitForEvent('loadedmetadata');

                const seekTime = Math.min(
                    VIDEO_THUMBNAIL_PREVIEW_SECOND,
                    Number.isFinite(video.duration) ? video.duration : 0,
                );
                if (seekTime > 0) {
                    video.currentTime = seekTime;
                    await waitForEvent('seeked');
                } else {
                    await waitForEvent('loadeddata');
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    throw new Error('Canvas context is unavailable');
                }

                ctx.drawImage(video, 0, 0, width, height);
                const dataUrl = canvas.toDataURL('image/png');
                if (isMounted) {
                    setThumbnail(dataUrl);
                }
            } catch (error) {
                if (error.message !== 'Thumbnail generation aborted') {
                    console.error('Failed to generate video thumbnail', error);
                }
            } finally {
                releaseVideo();
            }
        };

        const handleReleaseEvent = () => {
            abortGeneration();
        };

        window.addEventListener(
            RELEASE_VIDEO_THUMBNAILS_EVENT,
            handleReleaseEvent,
        );

        generateThumbnail();

        return () => {
            isMounted = false;
            window.removeEventListener(
                RELEASE_VIDEO_THUMBNAILS_EVENT,
                handleReleaseEvent,
            );
            abortGeneration();
        };
    }, [videoUrl, width, height]);

    if (!thumbnail) {
        return (
            <div className={styles.videoThumbnailPlaceholder}>
                <svg
                    className={styles.placeholder}
                    xmlns="http://www.w3.org/2000/svg"
                    width="80"
                    height="72"
                    viewBox="0 0 186 168"
                    fill="none"
                >
                    <path
                        d="M107.386 86.7865L81.9312 101.554C79.77 102.808 77 101.291 77 98.7688V69.2338C77 66.7113 79.77 65.1948 81.9312 66.4486L107.386 81.2161C109.538 82.4648 109.538 85.5378 107.386 86.7865Z"
                        fill="#2B6BE4"
                        fillOpacity="0.4"
                    />
                </svg>
            </div>
        );
    }

    return (
        <div className={styles.videoThumbnailWrapper}>
            <img
                src={thumbnail}
                alt={getText('msg_a2fc35af26ea')}
                className={styles.videoThumbnailImage}
            />
        </div>
    );
};
export const renderAssetPlaceholder = (fileType, url, name) => {
    const fileCategory = getFileCategory(fileType);

    if (fileCategory === 'image') {
        return <img src={url} alt={name} className={styles.assetImage} />;
    }
    if (fileCategory === 'video') {
        return (
            <div className={styles.videoPlaceholderContainer}>
                <VideoThumbnail videoUrl={url} />
            </div>
        );
    }
    if (fileCategory === 'audio') {
        return (
            <div className={styles.assetPlaceholderContainer}>
                <svg
                    className={`${styles.assetPlaceholder} ${styles['max-height-100']}`}
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M21.25 5C21.9404 5 22.5 5.55964 22.5 6.25V33.75C22.5 34.4404 21.9404 35 21.25 35C20.5596 35 20 34.4404 20 33.75V6.25C20 5.55964 20.5596 5 21.25 5ZM16.25 10C16.9404 10 17.5 10.5596 17.5 11.25V28.75C17.5 29.4404 16.9404 30 16.25 30C15.5596 30 15 29.4404 15 28.75V11.25C15 10.5596 15.5596 10 16.25 10ZM26.25 10C26.9404 10 27.5 10.5596 27.5 11.25V28.75C27.5 29.4404 26.9404 30 26.25 30C25.5596 30 25 29.4404 25 28.75V11.25C25 10.5596 25.5596 10 26.25 10ZM11.25 13.75C11.9404 13.75 12.5 14.3096 12.5 15V25C12.5 25.6904 11.9404 26.25 11.25 26.25C10.5596 26.25 10 25.6904 10 25V15C10 14.3096 10.5596 13.75 11.25 13.75ZM31.25 13.75C31.9404 13.75 32.5 14.3096 32.5 15V25C32.5 25.6904 31.9404 26.25 31.25 26.25C30.5596 26.25 30 25.6904 30 25V15C30 14.3096 30.5596 13.75 31.25 13.75ZM6.25 16.25C6.94036 16.25 7.5 16.8096 7.5 17.5V22.5C7.5 23.1904 6.94036 23.75 6.25 23.75C5.55964 23.75 5 23.1904 5 22.5V17.5C5 16.8096 5.55964 16.25 6.25 16.25ZM36.25 16.25C36.9404 16.25 37.5 16.8096 37.5 17.5V22.5C37.5 23.1904 36.9404 23.75 36.25 23.75C35.5596 23.75 35 23.1904 35 22.5V17.5C35 16.8096 35.5596 16.25 36.25 16.25Z"
                        fill="var(--iNGAGED-Primary-Color)"
                        fillOpacity="0.4"
                    />
                </svg>
            </div>
        );
    }
    if (['document', 'spreadsheet', 'other'].includes(fileCategory)) {
        return (
            <div className={styles.assetPlaceholderContainer}>
                <svg
                    className={`${styles.assetPlaceholder} ${styles['max-height-100']}`}
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M-88 0H-74.7678C-74.1047 0 -73.4688 0.263392 -73 0.732233L-63.7322 10C-63.2634 10.4688 -63 11.1047 -63 11.7678V35C-63 37.7614 -65.2386 40 -68 40H-88C-90.7614 40 -93 37.7614 -93 35V5C-93 2.23858 -90.7614 0 -88 0ZM-74.25 3.75V8.75C-74.25 10.1307 -73.1307 11.25 -71.75 11.25H-66.75L-74.25 3.75Z"
                        fill="var(--iNGAGED-Primary-Color)"
                        fillOpacity="0.4"
                    />
                </svg>
            </div>
        );
    }
    return null;
};

const ResourceProgressBar = ({ progressPercent }) => (
    <div className={styles.resourceProgressWrapper}>
        <div className={styles.resourceProgressTrack}>
            <div
                className={styles.resourceProgressFill}
                style={{ width: `${progressPercent}%` }}
            />
        </div>
        <span className={styles.resourceProgressLabel}>
            {Math.round(progressPercent)}%
        </span>
    </div>
);

const truncateName = (name, maxLength) => {
    if (name.length > maxLength) {
        return name.substring(0, maxLength) + '...';
    }
    return name;
};

/**
 * Custom Tooltip component.
 * Shows instantly on hover and focus, positioned above the target element.
 */
const Tooltip = ({ children, content }) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const wrapperRef = useRef(null);

    const showTooltip = () => {
        if (wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            setCoords({
                top: rect.top - 36,
                left: rect.left + rect.width / 2,
            });
            setVisible(true);
        }
    };

    const hideTooltip = () => {
        setVisible(false);
    };

    return (
        <>
            <span
                ref={wrapperRef}
                onMouseEnter={showTooltip}
                onFocus={showTooltip}
                onMouseLeave={hideTooltip}
                onBlur={hideTooltip}
                tabIndex={0}
                className={styles.tooltipTrigger}
                aria-describedby={visible ? 'tooltip-content' : undefined}
            >
                {children}
            </span>
            {visible && (
                <div
                    id="tooltip-content"
                    role="tooltip"
                    className={styles.tooltipContent}
                    style={{ top: coords.top, left: coords.left }}
                >
                    {content}
                </div>
            )}
        </>
    );
};

const AssetGridView = ({
    assets,
    handleClick,
    handleDoubleClick,
    handleClickOnDelete,
    activeCard,
    onIconClick,
    onViewClick,
    setActiveCard,
    isMyAssets,
    isGlobalAssets,
    propertiesModel = false,
    fullAssetName = true,
    isAssetLibraryReadOnly = false,
    isClientResourceView = false,
    hideGlobalAssetIcon = false,
    onResourceClick,
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 786);
    const [assetAvatars, setAssetAvatars] = useState({});

    const fetchIndividualFullName = async individualId => {
        try {
            const data = await getIndividualRecord(individualId);
            if (
                !data ||
                typeof data.firstName === 'undefined' ||
                typeof data.lastName === 'undefined'
            ) {
                return { name: 'Unknown Individual', avatarURL: null };
            }
            return {
                name: data.tenantUsers[0].fullName,
                avatarURL: data.tenantUsers[0].avatarUrl,
            };
        } catch (error) {
            console.error('Error fetching individual record:', error);
            return { name: 'Unknown Individual', avatarURL: null };
        }
    };

    useEffect(() => {
        const fetchAvatars = async () => {
            const avatarMap = {};
            for (const asset of assets) {
                if (isAssetPrivate(asset) && asset.individualId) {
                    const individualRecord = await fetchIndividualFullName(
                        asset.individualId,
                    );
                    avatarMap[asset.id] = individualRecord.avatarURL;
                }
            }
            setAssetAvatars(avatarMap);
        };

        if (assets.length > 0) {
            fetchAvatars();
        }
    }, [assets]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 786);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const checkIsActive = index => {
        return activeCard === index;
    };

    const isResourceCompleted = asset =>
        isClientResourceView &&
        asset.resourceProgress?.status === ResourceProgressStatusEnum.Completed;

    const showResourceProgressBar = asset => {
        if (!isClientResourceView) {
            return false;
        }

        const fileCategory = getFileCategory(asset.fileType);
        const isPlayableMedia =
            fileCategory === 'video' || fileCategory === 'audio';

        return (
            isPlayableMedia &&
            asset.resourceProgress?.status ===
                ResourceProgressStatusEnum.InProgress
        );
    };

    return (
        <div>
            <div
                className={
                    propertiesModel
                        ? styles.cardsContainer2
                        : styles.cardsContainer
                }
            >
                {assets.map((asset, index) => (
                    <div
                        key={asset.id}
                        className={`${
                            !isMobile
                                ? `${propertiesModel ? styles.assetCard2 : styles.assetCard} ${!isMobile && !isClientResourceView && checkIsActive(index) ? styles.active : ''}`
                                : `${styles.assetCardMobile}`
                        } ${styles['position-relative']}`}
                        onClick={
                            isClientResourceView
                                ? () => onResourceClick(asset)
                                : isMobile
                                  ? () => handleDoubleClick(index)
                                  : () => handleClick(index)
                        }
                        onDoubleClick={
                            !isMobile && !isClientResourceView
                                ? () => handleDoubleClick(index)
                                : undefined
                        }
                    >
                        <div className={styles.assetDisplay}>
                            {renderAssetPlaceholder(
                                asset.fileType,
                                asset.url,
                                asset.name,
                            )}
                            {!isMobile &&
                                !isClientResourceView &&
                                checkIsActive(index) &&
                                !propertiesModel && (
                                    <div className={styles.overlay}>
                                        <div className={styles.iconContainer}>
                                            <Tooltip
                                                content={getText(
                                                    'msg_69bd4ef9fbd0',
                                                )}
                                            >
                                                <span
                                                    className={styles.iconView}
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        onViewClick(asset);
                                                    }}
                                                >
                                                    <i className="bi bi-eye"></i>
                                                </span>
                                            </Tooltip>
                                            {!isAssetLibraryReadOnly &&
                                                (isMyAssets
                                                    ? asset.assetTypeId === 3 &&
                                                      !asset.isLocked
                                                    : asset.assetTypeId !==
                                                      3) && (
                                                    <Tooltip
                                                        content={getText(
                                                            'msg_f6fdbe48dc54',
                                                        )}
                                                    >
                                                        <span
                                                            className={
                                                                styles.iconDelete
                                                            }
                                                            onClick={event => {
                                                                event.stopPropagation();
                                                                handleClickOnDelete(
                                                                    asset,
                                                                );
                                                            }}
                                                        >
                                                            <i className="bi bi-trash3"></i>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            <Tooltip
                                                content={getText(
                                                    'msg_bc6c88db2f07',
                                                )}
                                            >
                                                <span
                                                    className={
                                                        styles.iconProperties
                                                    }
                                                    onClick={event => {
                                                        onIconClick(event);
                                                    }}
                                                >
                                                    <i className="bi bi-three-dots"></i>
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </div>
                                )}
                        </div>
                        {/* Replaced native title attribute with custom Tooltip */}
                        <Tooltip content={asset.name}>
                            <span className={styles.assetTitle}>
                                {fullAssetName
                                    ? asset.name
                                    : truncateName(asset.name, 7)}
                            </span>
                        </Tooltip>
                        {!isMobile && (
                            <>
                                <div className={styles.assetCardBottomRow}>
                                    {showResourceProgressBar(asset) ? (
                                        <ResourceProgressBar
                                            progressPercent={
                                                asset.resourceProgress
                                                    ?.progressPercent || 0
                                            }
                                        />
                                    ) : (
                                        <span className={styles.assetType}>
                                            {asset.fileType}
                                        </span>
                                    )}
                                    {isResourceCompleted(asset) && (
                                        <i
                                            className={`${styles.resourceCompletedIcon} bi bi-check-circle-fill`}
                                            title={getText('msg_1798b3ba42ee')}
                                        />
                                    )}
                                    {asset.isHidden && (
                                        <i
                                            className={`${styles.hiddenAssetIcon} bi bi-eye-slash-fill`}
                                            title={getText('msg_b48be28b71d2')}
                                        />
                                    )}
                                    {!isGlobalAssets &&
                                        !hideGlobalAssetIcon &&
                                        (asset.isGlobalAsset ? (
                                            <i
                                                className={`${styles.globalAssetIcon} bi bi-globe`}
                                            />
                                        ) : (
                                            asset.assetTypeId === 1 && (
                                                <i
                                                    className={`${styles.contentResourceIcon} bi bi-bookmark-fill`}
                                                />
                                            )
                                        ))}
                                </div>
                                {checkIsActive(index) && propertiesModel && (
                                    <i
                                        className={`bi bi-plus-circle-fill ${styles.propertiesAddIcon}`}
                                    />
                                )}
                            </>
                        )}
                        {isAssetPrivate(asset) && (
                            <Avatar
                                src={assetAvatars[asset.id] ?? ''}
                                className={classNames(
                                    styles.privateAssetAvatar,
                                    {
                                        [styles.privateAssetAvatarMobile]:
                                            isMobile,
                                        [styles.privateAssetAvatarDesktop]:
                                            !isMobile,
                                    },
                                )}
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AssetGridView;
